import React from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { DashboardWidget, WidgetDataResult } from '../../../redux/wallboardApi';
import { useDashboardContext, WIDGET_COLORS } from '../pages/DashboardViewPage';

/**
 * Line Graph Widget
 * 
 * Displays data as a line or area chart.
 * Supports dynamic font sizing and themed colors.
 */
interface LineGraphWidgetProps {
  widget: DashboardWidget;
  data?: WidgetDataResult;
  isArea?: boolean;
  colorIndex?: number;
}

export const LineGraphWidget: React.FC<LineGraphWidgetProps> = ({
  widget,
  data,
  isArea = false,
  colorIndex = 0,
}) => {
  const displayOptions = widget.config?.displayOptions || {};
  const { fontSize } = useDashboardContext();
  
  // Get color theme based on index
  const colorTheme = WIDGET_COLORS[colorIndex % WIDGET_COLORS.length];
  // Use valueColor for text on white background, header for chart lines
  const textColor = colorTheme.valueColor || colorTheme.header;
  const chartColor = colorTheme.header;

  // Check if data is single value (no groupBy configured)
  const isSingleValue = data?.data && 
    typeof data.data === 'object' && 
    !Array.isArray(data.data) && 
    'value' in data.data && 
    !('labels' in data.data);

  // Parse data
  const { xAxisData, seriesData } = React.useMemo(() => {
    if (!data?.data) return { xAxisData: [], seriesData: [] };

    // Handle grouped data format (when groupBy is configured)
    if (data.data.labels && data.data.values) {
      return {
        xAxisData: data.data.labels as string[],
        seriesData: data.data.values as number[],
      };
    }

    // Handle array format
    if (Array.isArray(data.data)) {
      return {
        xAxisData: data.data.map((item: any, i: number) => 
          item.label || item.name || item.date || `Point ${i + 1}`
        ),
        seriesData: data.data.map((item: any) => 
          item.value || item.count || 0
        ),
      };
    }

    return { xAxisData: [], seriesData: [] };
  }, [data]);

  // Single value - need groupBy for line charts
  if (isSingleValue) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 2,
        }}
      >
        <Typography 
          sx={{ 
            color: textColor, 
            fontSize: `${1.5 * fontSize}rem`,
            fontWeight: 'bold',
            mb: 1,
          }}
        >
          {(data?.data?.value ?? 0).toLocaleString()}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: `${0.75 * fontSize}rem` }}>
          Configure &quot;Group By&quot; (e.g., Month) in widget settings for time series chart
        </Typography>
      </Box>
    );
  }

  // No data
  if (xAxisData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: `${0.875 * fontSize}rem` }}>No data available</Typography>
      </Box>
    );
  }

  // Format value for tooltip/labels
  const valueFormatter = (value: number | null) => {
    if (value === null) return '';
    const unit = widget.config?.unit || '';
    if (unit === '$') {
      return `$${value.toLocaleString()}`;
    }
    return `${value.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  };

  // Calculate font sizes based on fontSize multiplier
  const tickFontSize = Math.round(11 * fontSize);

  // Calculate appropriate left margin based on max value length
  const maxValue = Math.max(...seriesData, 0);
  const maxValueLength = maxValue.toLocaleString().length;
  const leftMargin = Math.max(60, 20 + maxValueLength * 8);

  // Calculate max label length to determine rotation and bottom margin
  const maxLabelLength = Math.max(...xAxisData.map(label => String(label).length));
  const needsRotation = xAxisData.length > 7 || maxLabelLength > 10;
  // More bottom margin for rotated labels, scale with label length
  const bottomMargin = needsRotation ? Math.min(120, 50 + maxLabelLength * 4) : 50;

  // Truncate long labels for display
  const truncatedLabels = xAxisData.map(label => {
    const str = String(label);
    return str.length > 20 ? str.substring(0, 18) + '...' : str;
  });

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 200, overflow: 'visible' }}>
      <LineChart
        xAxis={[{
          data: truncatedLabels,
          scaleType: 'point',
          tickLabelStyle: {
            angle: needsRotation ? 45 : 0,
            textAnchor: needsRotation ? 'start' : 'middle',
            fontSize: tickFontSize,
          },
          tickLabelInterval: () => true, // Show ALL x-axis labels
        }]}
        yAxis={[{
          tickLabelStyle: {
            fontSize: tickFontSize,
          },
        }]}
        series={[{
          data: seriesData,
          valueFormatter,
          area: isArea,
          color: chartColor,
          showMark: seriesData.length <= 20,
        }]}
        margin={{ top: 20, bottom: bottomMargin, left: leftMargin, right: 20 }}
        grid={{ horizontal: true }}
        hideLegend
      />
    </Box>
  );
};

export default LineGraphWidget;
